import React, { useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IGetWarehousesDTO from "../../../models/DTO/IGetWarehousesDTO";
import IOnGetWarehousesDTO from "../../../models/DTO/IOnGetWarehousesDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IOwTableCols from "../../../models/IOwTableCols";
import IWarehouse from "../../../models/IWarehouse";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwButton from "../../shared/OwButton/OwButton";
import OwGoogleStaticMap from "../../shared/OwGoogleStaticMap/OwGoogleStaticMap";
import OwTable from "../../shared/OwTable/OwTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import "./Warehouses.scss";
import WarehouseSidebar from "../WarehouseSidebar/WarehouseSidebar";
import OwActionBar from "../../shared/OwActionBar/OwActionBar";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IAction from "../../../models/IAction";
import IShopifyPullLocationsDTO from "../../../models/DTO/IShopifyPullLocationsDTO";
import IShopifyOnPullLocationsDTO from "../../../models/DTO/IShopifyOnPullLocationsDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import IntegrationsConnectedBar from "../../shared/IntegrationsConnectedBar/IntegrationsConnectedBar";
import IQBDCreateWarehousesDTO from "../../../models/DTO/IQBDCreateWarehousesDTO";
import IQBDOnCreateWarehousesDTO from "../../../models/DTO/IQBDOnCreateWarehousesDTO";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import IOnProcessWarehouseDTO from "../../../models/DTO/IOnProcessWarehouseDTO";
import IProcessWarehouseDTO from "../../../models/DTO/IProcessWarehouseDTO";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { Socket } from "socket.io-client";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";

type Props = {};

export const Warehouses: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const getBreadcrumbs = () => {
        return [
            {
                title: "dashboard",
                url: "/dashboard",
            },
            {
                title: "inventory",
                url: "/inventory",
            },
            {
                title: "warehouses",
                url: "/inventory/warehouses",
            },
        ];
    };

    const renderActionsColFn = (row: IWarehouse) => {
        return (
            <div>
                <OwIconButton iconClass={"fas fa-trash-alt me-2"} iconColorClass={"text-danger"} fn={() => deleteWarehouse(row)} />
                <OwIconButton iconClass={"fas fa-pencil-alt me-2"} iconColorClass={"text-primary"} fn={() => openWarehouseSidebar(row)} />
            </div>
        );
    };

    const renderNameColFn = (row: IWarehouse) => {
        return (
            <div>
                <div>{row.name}</div>
                {row.is_hidden ? (
                    <div>
                        <OwLabel title={"hidden"} leftIconClass={"fad fa-eye"} className={"text-muted"} />
                    </div>
                ) : null}
            </div>
        );
    };

    const renderAddressColFn = (row: IWarehouse) => {
        return (
            <div className={"d-flex"}>
                {supplierModel.has_google_maps ? <OwGoogleStaticMap address={row.Address!} className={"me-2"} size={"120x120"} /> : null}
                <AddressDisplay address={row.Address} />
            </div>
        );
    };

    const renderIntegrationColFn = (row: IWarehouse) => {
        return (
            <div>
                <IntegrationsConnectedBar
                    supplierModel={supplierModel}
                    showIntegrations={{
                        shopify: supplierModel.Supplier_Integration.has_shopify,
                        quickbooksDesktop: supplierModel.Supplier_Integration.has_qbd,
                    }}
                    shopifyConnected={row.Warehouse_Shopify_Maps?.length}
                    qbdConnected={!!row.qbd_ListID}
                />
            </div>
        );
    };

    const getTableCols = () => {
        return [
            {
                isActionsCol: true,
                title: "",
                valueProp: "",
                renderFn: renderActionsColFn,
                renderFnEmitsRow: true,
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: renderNameColFn,
                renderFnEmitsRow: true,
            },
            {
                title: "address",
                valueProp: "",
                renderFn: renderAddressColFn,
                renderFnEmitsRow: true,
            },
            {
                title: "integrations",
                valueProp: "",
                renderFn: renderIntegrationColFn,
                renderFnEmitsRow: true,
            },
        ];
    };

    const getActions = (): IAction[] => {
        const shopifyActions: IAction = {
            isGroup: true,
            name: "shopify",
            groupActions: [
                {
                    fn: () => {
                        pullShopifyLocations();
                    },
                    name: "pull_locations",
                    isActive: () => shopifyActive,
                    disabledFn: () => {
                        return shopifyActive;
                    },
                },
            ],
        };

        const qbdActions: IAction = {
            isGroup: true,
            name: "quickbooks_desktop",
            groupActions: [
                {
                    fn: () => {
                        updateQbdWarehouses();
                    },
                    name: "update_warehouses",
                    isActive: () => qbdActive,
                    disabledFn: () => {
                        return qbdActive;
                    },
                },
            ],
        };

        const _actions: IAction[] = [];

        if (supplierModel.Supplier_Integration.has_shopify) {
            _actions.push(shopifyActions);
        }

        if (supplierModel.Supplier_Integration.has_qbd) {
            _actions.push(qbdActions);
        }

        return _actions;
    };

    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isInitialData, setIsInitialData] = useState(false);
    const [pagination, setPagination] = useState({ offset: 0, limit: 100 });
    const [warehouses, setWarehouses] = useState<IWarehouse[]>([]);
    const [totalWarehouses, setTotalWarehouses] = useState(0);
    const [crumbs, setCrumbs] = useState<IBreadcrumbs[]>(getBreadcrumbs());
    const [columns, setColumns] = useState<IOwTableCols[]>(getTableCols());
    const [actions, setActions] = useState<IAction[]>(getActions());
    const [shopifyActive, setShopifyActive] = useState(false);
    const [qbdActive, setQbdActive] = useState(false);

    useEffect(() => {
        socket.on(EndpointsEnum.WarehouseOnGetWarehouses, onGetWarehouses);
        socket.on(EndpointsEnum.ShopifyOnPullLocations, onPullShopifyLocations);
        socket.on(EndpointsEnum.QBDOnCreateWarehouses, onUpdateQbdWarehouses);
        socket.on(EndpointsEnum.WarehouseOnProcessWarehouse, onProcessWarehouse);

        getWarehouses();

        return () => {
            socket.removeListener(EndpointsEnum.WarehouseOnGetWarehouses, onGetWarehouses);
            socket.removeListener(EndpointsEnum.ShopifyOnPullLocations, onPullShopifyLocations);
            socket.removeListener(EndpointsEnum.QBDOnCreateWarehouses, onUpdateQbdWarehouses);
            socket.removeListener(EndpointsEnum.WarehouseOnProcessWarehouse, onProcessWarehouse);
        };
    }, []);

    useEffect(() => {
        setColumns(getTableCols());
    }, [warehouses]);

    const updateQbdWarehouses = () => {
        const payload: IQBDCreateWarehousesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBDCreateWarehouses, payload);

        setQbdActive(true);
    };

    const onUpdateQbdWarehouses = (data: IQBDOnCreateWarehousesDTO) => {
        const notification: INotification = {
            message: "successfully_updated_qbd_warehouses",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        setQbdActive(false);

        getWarehouses();
    };

    const pullShopifyLocations = () => {
        const payload: IShopifyPullLocationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.ShopifyPullLocations, payload);

        setShopifyActive(true);
    };

    const onPullShopifyLocations = (data: IShopifyOnPullLocationsDTO) => {
        const notification: INotification = {
            message: "successfully_pulled_shopify_locations",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        setShopifyActive(false);

        getWarehouses();
    };

    const processWarehouse = (row: IWarehouse, deleteFlag: boolean) => {
        const payload: IProcessWarehouseDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            warehouse: row,
            deleteFlag,
        };

        socket.emit(EndpointsEnum.WarehouseProcessWarehouse, payload);
    };

    const onProcessWarehouse = (data: IOnProcessWarehouseDTO) => {
        getWarehouses();
    };

    const deleteWarehouse = (row: IWarehouse) => {
        processWarehouse(row, true);
    };

    const getWarehouses = () => {
        const payload: IGetWarehousesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.WarehouseGetWarehouses, payload);

        setIsDataFetching(true);
    };

    const onGetWarehouses = (data: IOnGetWarehousesDTO) => {
        setIsInitialData(false);
        setIsDataFetching(false);
        setWarehouses(data.warehouses);
        setTotalWarehouses(data.warehouses.length);
    };

    const cancelSidebar = (refresh?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refresh) {
            getWarehouses();
        }
    };

    const openWarehouseSidebar = (warehouse?: IWarehouse) => {
        const component = (
            <WarehouseSidebar
                cancelFn={cancelSidebar}
                notifyFn={uiContext.notifyFn}
                socket={socket}
                userModel={userModel}
                supplierModel={supplierModel}
                warehouse={warehouse}
            />
        );

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const paginationChanged = (offset: number, limit: number) => {
        setPagination({ offset, limit });
    };

    return (
        <div className="Warehouses">
            <Breadcrumbs crumbs={crumbs} />

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={"warehouses"} leftIconName={"fad fa-warehouse-alt"} />
                    <div>
                        <OwButton
                            className={"ms-2 btn-warning"}
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            fn={() => {
                                openWarehouseSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div>
                <OwActionBar
                    columns={{}}
                    colToggledFn={() => {}}
                    toggleSmallTable={() => {}}
                    actions={actions}
                    totalValue={totalWarehouses}
                />
            </div>

            <div className="row mt-3">
                <div className="col-12">
                    <OwTable
                        columns={columns}
                        dataSet={warehouses}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={isDataFetching}
                        initialData={isInitialData}
                        tableConfig={{ tableStriped: true, hasDetailView: true }}
                    />
                </div>
            </div>

            <div className={"row"}>
                <div className={"col-12"}>
                    <PaginationBar
                        offset={pagination.offset}
                        limit={pagination.limit}
                        totalRecords={totalWarehouses}
                        onPaginationChanged={paginationChanged}
                    />
                </div>
            </div>
        </div>
    );
};
